// @ts-nocheck
import translate from "@insite/client-framework/Translate";

import Modal from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import useLocalizeCurrency from "@store/Framework/Common/Hooks/useLocalizeCurrency";
import useProductContextsBySkus from "@store/Framework/Common/Hooks/useProductContextsBySku";
import { InvoiceDetailModel } from "@store/Framework/Services/Reporting/ReportingModels";
import { InvoiceDetail, InvoiceSummaryData } from "@store/Framework/Types/types";
import AddToCartAndWishList from "@store/Library/Components/Reporting/AddToCartAndWishList/AddtoCartAndWishList";
import ReportDataTable, {
    DataTableData,
    FormatDictionary,
} from "@store/Library/Components/Reporting/ApiDataTable/ReportDataTable";
import { invoiceSummaryHeaders, invoiceSummaryKeys } from "@store/Library/Widgets/Reports/InvoiceDetailSearchForm";
import useReportingProductLinkFormatting from "@store/Library/Widgets/Reports/useReportingLinkFormatting";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

const desktopQuery = "@media (min-width: 768px)";
const mobileQuery = "@media (max-width: 767px)";
const ApiModal = styled(Modal)`
    && {
        & div[role="dialog"] {
            border-radius: 0.5rem;
            ${desktopQuery} {
                max-width: 95vw;
            }
        }
    }
`;
const ModalHeader = styled.div`
    && {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        ${mobileQuery} {
            row-gap: 1rem;
            flex-direction: column;
            > * {
                text-align: center;
            }
        }
        & h4 {
            margin: 0 2rem 0 0 !important;
        }
        & button {
            margin-left: auto;
            margin-right: 2rem;
        }
    }
`;

const Hr = styled.hr`
    margin: 1.5rem -1.875rem;
    border: 1px solid rgba(74, 74, 74, 0.2);
`;
export const invoiceDetailHeaders = ["Item #", "Product", "Quantity", "Unit", "Unit Price", "Extended Price"];
export const invoiceDetailKeys: (keyof InvoiceDetail)[] = [
    "itemnumber",
    "itemdesc",
    "quantity",
    "unitofmeasure",
    "unitprice",
    "extendedprice",
];

interface Props {
    invoiceSummary?: InvoiceSummaryData;
    invoiceSummaryDictionary: FormatDictionary;
    isOpen: boolean;
    handleClose: () => void;
    invoiceDetails?: InvoiceDetailModel;
}
const InvoiceDetailModal: React.FC<Props> = ({
    invoiceDetails,
    invoiceSummary,
    invoiceSummaryDictionary,
    isOpen,
    handleClose,
}) => {
    const { productContextsBySku, getProductContextsBySkus } = useProductContextsBySkus();
    const { getProductLinkFieldByName, getProductLinkBySku } = useReportingProductLinkFormatting(
        "itemdesc",
        "itemnumber",
        invoiceDetails?.items,
        productContextsBySku,
    );
    const [elements, setElements] = useState<JSX.Element[]>([]);
    const LocalizedCurrency = useLocalizeCurrency();
    const formatDictionary: FormatDictionary = {
        unitprice: LocalizedCurrency,
        extendedprice: LocalizedCurrency,
        itemnumber: getProductLinkBySku,
        itemdesc: getProductLinkFieldByName,
    };

    const invoiceDetailData =
        invoiceDetails?.items.map((detail, index) => ({ ...detail, extraElement: elements?.[index] })) || [];

    useEffect(() => {
        if (invoiceDetailData.length) {
            getProductContextsBySkus(invoiceDetailData.map(item => item.itemnumber));
        }
    }, [invoiceDetailData.length]);
    useEffect(() => {
        if (invoiceDetailData.length) {
            const itemNumbers = invoiceDetailData.map(item => item.itemnumber.trim());
            const elements = itemNumbers.map(itemNumber => {
                return (
                    <AddToCartAndWishList
                        productContextsBySku={productContextsBySku}
                        key={itemNumber}
                        sku={itemNumber}
                    />
                );
            });
            setElements(elements);
        }
    }, [productContextsBySku, invoiceDetailData.length]);
    if (!invoiceSummary || !invoiceDetails) {
        return null;
    }
    const invoiceSummaryDataTableData: DataTableData<InvoiceSummaryData> = {
        title: "Invoice Detail Report",
        data: [invoiceSummary],
        headers: invoiceSummaryHeaders,
        dataKeys: invoiceSummaryKeys,
        dataSummaryTitle: translate("Invoices"),
        formatDictionary: invoiceSummaryDictionary,
    };
    const invoiceDetailTableData: DataTableData<InvoiceDetail> = {
        title: "Invoice Detail Report",
        data: invoiceDetailData,
        headers: invoiceDetailHeaders,
        dataKeys: invoiceDetailKeys,
        dataSummaryTitle: translate("Items"),
        uniqueKey: "itemnumber",
        formatDictionary,
    };

    const modalHeader = (
        <ModalHeader>
            <Typography variant="h4">{invoiceSummary.customername}</Typography>
            <Typography variant="h4">
                {translate("Invoice")} # {invoiceSummary.invoicenum}
            </Typography>
            <Typography variant="h4">
                {translate("Total")} {LocalizedCurrency(invoiceSummary.ordertotal)}
            </Typography>
        </ModalHeader>
    );

    return (
        <ApiModal headline={modalHeader} isOpen={isOpen} handleClose={handleClose}>
            <ReportDataTable searchable={false} showExportButton={false} dataTableData={invoiceSummaryDataTableData} />
            <Hr />
            <ReportDataTable dataTableData={invoiceDetailTableData} />
        </ApiModal>
    );
};

export default InvoiceDetailModal;
